import { findIndex } from 'lodash';
import { type RestMultiRowSurveyCard } from '../contracts';

export type FunnelSurvey = RestMultiRowSurveyCard['provides'];

/**
 * `provides` is intersected with `Record<string, unknown>`, so mapping over its
 * keys directly would readmit any key and any value. Dropping the index
 * signature leaves the declared fields, and keeps them in step with the card
 * type rather than restating them here.
 */
type DeclaredFields<T> = {
  [
    K in keyof T as string extends K ? never : number extends K ? never : K
  ]: T[K];
};

type SurveyFields = DeclaredFields<FunnelSurvey>;

/**
 * One variant per survey field, so a key can only be paired with a value that
 * field actually accepts.
 */
type SetSurveyFieldAction = {
  [K in keyof SurveyFields]-?: {
    type: 'setSurveyField';
    key: K;
    value: SurveyFields[K];
  };
}[keyof SurveyFields];

export type SurveyAction =
  | SetSurveyFieldAction
  | {
      type: 'setReasonForLeaving';
      name: string;
      value: string;
      tier: string;
      livesOn: string;
    }
  | { type: 'clearCompetitionOther' }
  | { type: 'clearReasonOther' }
  | { type: 'setModalResponse'; modalName: string; message?: string };

export const reduceSurvey = (
  survey: FunnelSurvey,
  action: SurveyAction
): FunnelSurvey => {
  switch (action.type) {
    case 'setSurveyField':
      return { ...survey, [action.key]: action.value };

    case 'setReasonForLeaving': {
      const { name, value, tier, livesOn } = action;
      const entry = { name, value, tier, lives_on: livesOn };
      const reasons = survey.reason_for_leaving ?? [];
      const index = findIndex(reasons, { name, lives_on: livesOn });
      return {
        ...survey,
        reason_for_leaving:
          index === -1
            ? [...reasons, entry]
            : reasons.map((reason, at) => (at === index ? entry : reason)),
      };
    }

    // These clears historically differ: one deletes the key, the other keeps it.
    case 'clearCompetitionOther': {
      const updated = { ...survey };
      delete updated.competition_other;
      return updated;
    }

    case 'clearReasonOther':
      return { ...survey, reason_other: undefined };

    case 'setModalResponse':
      return {
        ...survey,
        responses: {
          ...(survey.responses || {}),
          [action.modalName]: action.message,
        },
      };

    default: {
      const unhandled: never = action;
      throw new Error(`Unhandled survey action: ${JSON.stringify(unhandled)}`);
    }
  }
};
